import { Injectable, Injector, NgZone } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { StorageService } from '../storage-service/storage.service';
import { StorageKeys } from '../../enums/storage.enum';
import { AuthService } from 'src/app/home/auth/auth.service';

@Injectable()
export class HttpInterceptorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private readonly storageService: StorageService,
    private readonly router: Router,
    private readonly injector: Injector,
    private readonly ngZone: NgZone
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Bypass auth and refresh-token requests to prevent infinite loops
    if (
      request.url.includes('/v1/auth/refresh-token') ||
      request.url.includes('/v1/auth/token-using-otp') ||
      request.url.includes('/v1/auth/generate-login-otp')
    ) {
      return next.handle(request);
    }

    const authService = this.injector.get(AuthService);
    const refreshToken = this.storageService.getDataFromLocalStorage(
      StorageKeys.REFRESH_TOKEN
    );

    // Proactive refresh: If token is past refresh time, trigger refresh before sending request
    if (refreshToken && authService.needsTokenRefresh()) {
      return this.handleTokenRefresh(request, next, authService, refreshToken);
    }

    const token = this.storageService.getDataFromLocalStorage(
      StorageKeys.ACCESS_TOKEN
    );

    let authReq = request;
    if (token) {
      authReq = this.addToken(request, token);
    }

    return next.handle(authReq).pipe(
      catchError((error: any) => {
        // If request fails with 401, redirect to login immediately
        if (error.status === 401 || error.statusCode === 401) {
          this.storageService.clearLocalStorage();
          this.storageService.clearSessionStorage();
          this.ngZone.run(() => {
            this.router.navigate(['/login']);
          });
        }
        return throwError(() => error);
      })
    );
  }

  private handleTokenRefresh(
    request: HttpRequest<any>,
    next: HttpHandler,
    authService: AuthService,
    refreshToken: string
  ): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      // If refresh is already in progress, wait on readonly refreshTokenSubject
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addToken(request, token));
        })
      );
    } else {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return authService.getRefreshToken(refreshToken).pipe(
        switchMap((response: any) => {
          const newToken = response.data.token;
          const newRefreshToken = response.data.refreshToken;
          this.storageService.setDataToLocalStorage(StorageKeys.ACCESS_TOKEN, newToken);
          if (newRefreshToken) {
            this.storageService.setDataToLocalStorage(StorageKeys.REFRESH_TOKEN, newRefreshToken);
          }
          authService.updateSessionTimes();
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(request, newToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.storageService.clearLocalStorage();
          this.storageService.clearSessionStorage();
          this.ngZone.run(() => {
            this.router.navigate(['/login']);
          });
          return throwError(() => err);
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    const userDetails = this.storageService.getDataFromLocalStorage(
      StorageKeys.SELF_DETAILS
    );
    let headers = request.headers.set('Authorization', `Bearer ${token}`);
    if (userDetails?.company) {
      headers = headers.set('presentCompany', userDetails.company);
    }
    return request.clone({
      headers: headers,
    });
  }
}
